<template>
  <div>
    <CCard class="mt-4">
      <CCardBody class="p-3">
        <h5 class="mb-3">Delivery Distance Configuration</h5>

        <div class="delivery-settings">
          <!-- First Group -->
          <div class="settings-group">
            <div class="settings-flex">
              <div class="setting-item small-item">
                <label>Enable Fee</label>
                <CSwitch
                  color="success"
                  :checked.sync="delivery_distances.fee_enabled"
                  labelOn="Yes"
                  labelOff="No"
                  @update:checked="(val) => {
                    if (!val) {
                      delivery_distances.based_fee = 0;
                      delivery_distances.threshold_meter = 0;
                    } else {
                      delivery_distances.threshold_meter = 1000;
                    }
                  }"
                />
              </div>

              <div class="setting-item medium">
                <label>Base Delivery Fee</label>
                <CInput
                  type="number"
                  :disabled="!delivery_distances.fee_enabled"
                  :value.sync="delivery_distances.based_fee"
                  min="0"
                  max="50"
                  step="0.5"
                  placeholder="Base fee"
                >
                  <template #prepend-content><CIcon name="cil-euro" /></template>
                </CInput>
              </div>

              <div class="setting-item large">
                <label>Threshold Distance <small class="text-muted ">(meters)</small></label>
                <div class="vertical-center">
                  <CInput
                    type="range"
                    :disabled="!delivery_distances.fee_enabled"
                    :value.sync="delivery_distances.threshold_meter"
                    min="1000"
                    max="20000"
                    step="500"
                    placeholder="Enter threshold distance"
                    class="mb-0"
                    style="flex: 1"
                  />
                  <span>{{ delivery_distances.threshold_meter }} m.</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Second Group -->
          <div class="settings-group">
            <div class="settings-flex">
              <div class="setting-item large">
                <label>Maximum Delivery Distance <small class="text-muted ">(meters)</small></label>
                <div class="vertical-center">
                  <CSwitch
                    color="success"
                    :checked.sync="delivery_distances.max_distance_enabled"
                    labelOn="On"
                    labelOff="Off"
                    @update:checked="(val) => val ? delivery_distances.max_distance_meter = 1000 : delivery_distances.max_distance_meter = 0"
                  />
                  <CInput
                    class="mb-0"
                    type="range"
                    :disabled="!delivery_distances.max_distance_enabled"
                    :value.sync="delivery_distances.max_distance_meter"
                    min="1000"
                    max="20000"
                    step="500"
                    style="flex: 1"
                  />
                  <span>{{ delivery_distances.max_distance_meter }} m.</span>
                </div>
              </div>

              <div class="setting-item medium" :class="{ 'disable-item': !delivery_distances.hasdeliveryService }">
                <label>Lock Maximum Delivery Distance <small class="text-muted ">(prevent hub override)</small></label>
                <CSwitch
                  color="danger"
                  :checked.sync="delivery_distances.lock_max_distance_meter"
                  labelOn="Yes"
                  labelOff="No"
                />
              </div>
            </div>
          </div>

          <!-- Third Group - Delivery Service Distance Threshold -->
          <div class="settings-group" v-if="delivery_distances.hasdeliveryService">
            <div class="settings-flex">
              <div class="setting-item large">
                <label>Delivery Service Distance Threshold <small class="text-muted ">(meters)</small></label>
                <div class="vertical-center">
                  <CSwitch
                    color="success"
                    :checked.sync="delivery_distances.service_distance_threshold_enabled"
                    labelOn="On"
                    labelOff="Off"
                    @update:checked="(val) => {
                      if (!val) {
                        delivery_distances.service_distance_threshold_meter = null;
                      } else {
                        delivery_distances.service_distance_threshold_meter = 1000;
                      }
                    }"
                  />
                  <CInput
                    class="mb-0"
                    type="range"
                    :disabled="!delivery_distances.service_distance_threshold_enabled"
                    :value.sync="delivery_distances.service_distance_threshold_meter"
                    min="500"
                    max="10000"
                    step="100"
                    style="flex: 1"
                  />
                  <span>{{ delivery_distances.service_distance_threshold_meter || 0 }} m.</span>
                </div>
                <small class="text-muted d-block mt-2">
                  Orders within this distance will be delivered by Menute couriers. Orders beyond this distance will be delivered by restaurant's own couriers.
                </small>
              </div>

              <div class="setting-item medium d-none d-xl-block"></div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody class="p-3">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <h5 class="mb-2 mb-md-0">Delivery Zones</h5>
          <CButton
            color="info"
            size="sm"
            variant="outline"
            @click="mapModal = true"
          >
            <CIcon name="cil-map" />
            Show on Map
          </CButton>
        </div>

        <CAlert
          v-if="deliveryZoneGapWarning.suggestedZones.length"
          color="warning"
          class="delivery-zone-gap-warning"
          show
        >
          <div class="d-flex flex-column flex-lg-row justify-content-between">
            <div>
              <strong>{{ deliveryZoneGapWarningTitle }}</strong>
              <span>
                {{ deliveryZoneGapWarningSummary }}
              </span>
              <div class="mt-2">
                <CBadge
                  v-for="zone in visibleDeliveryZoneGapSuggestions"
                  :key="`${zone.zcCountryId}-${zone.zcZip}`"
                  color="warning"
                  class="mr-1 mb-1"
                >
                  {{ zone.zcZip }} {{ zone.zcCity }} ({{ zone.distance | FixedNumber }} km)
                </CBadge>
                <CBadge
                  v-if="remainingDeliveryZoneGapSuggestionCount > 0"
                  color="secondary"
                  class="mr-1 mb-1"
                >
                  +{{ remainingDeliveryZoneGapSuggestionCount }} more
                </CBadge>
              </div>
            </div>

            <div class="mt-3 mt-lg-0 ml-lg-3">
              <CButton
                color="warning"
                size="sm"
                @click="addSuggestedDeliveryZones"
              >
                <CIcon name="cil-plus" />
                Add suggested zones
              </CButton>
            </div>
          </div>
        </CAlert>

        <div class="row">
          <!-- Country Filter -->
          <div class="col-lg-4 mb-2">
            <v-select
              class="v-select-filter"
              placeholder="Country.."
              :value="country"
              :options="authCountries"
              :reduce="c => c.value"
              :searchable="false"
              @input="countryFilter"
            />
          </div>

          <div class="col-lg mb-2">
            <v-select
              class="v-select-filter"
              placeholder="Please select a region"
              label="zcCity"
              v-model="selected_zip_code"
              :options="groupedZipCodeOptions"
              :reduce="zc => ({id: zc.id, zcZip: zc.zcZip, zcCity: zc.zcCity, zcCountryId: zc.zcCountryId})"
              :filter="filteredZipCodes"
              :loading="loading"
              @input="selectingZipCodes"
            >
              <template v-slot:option="zip_code">
                <strong>{{ zip_code.zcZip }}</strong>
                <span v-if="zip_code.zcCity"> - {{ zip_code.zcCity }} </span>
              </template>
              <span slot="no-options">Sorry, no matching options.</span>
            </v-select>
          </div>

          <div class="col-lg-12 mb-2 mb-lg-0">
            <div class="d-flex flex-column flex-md-row align-items-md-center">
              <CButton
                size="sm"
                color="link"
                class="p-0 mr-2"
                @click="getZipCodesNearBy"
              >
                <CIcon name="cil-plus" /> Add delivery zones within {{maxDistanceNearBy}} km
              </CButton>

              <CInput
                class="m-0"
                type="range"
                :value.sync="maxDistanceNearBy"
                min="1" max="20" step="1"
              />

              <CInputCheckbox
                custom
                class="mt-2 mt-md-0 ml-md-3"
                label="Nauwkeuriger met postcodegrenzen"
                :checked.sync="useDeliveryCircleSuggestions"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-12">
            <CDataTable
              class="table-sm table-zip-codes"
              :items="deliveryZoneRows"
              :fields="fields"
              :noItemsView="{
                noResults: 'No filtering results are available!',
                noItems: 'No linked ZIP codes found!',
              }"
              @row-clicked="(item) => selected_row = item"
            >
              <!-- Distance -->
              <template #distance="{ item }">
                <td class="align-middle">
                  <CIcon v-if="item._classes === 'table-warning'" name="cil-warning" size="lg" />
                  {{ item.distance | FixedNumber }}
                </td>
              </template>

              <!-- Country -->
              <template #country="{ item }">
                <td class="align-middle">
                  <CIcon class="mr-1" :content="$options.flagSet[item.country.flag]" size="lg" />
                  {{ item.country.label }}
                </td>
              </template>

              <!-- Zone -->
              <template #zone="{ item }">
                <td class="align-middle">
                  <strong>{{ item.zcZip }}</strong>
                  <small class="ml-1">{{ item.zcCity }}</small>
                  <CBadge v-if="item.conditions_inconsistent" color="warning" class="ml-1">
                    Conditions differ
                  </CBadge>
                </td>
              </template>

              <!-- Delivery Charge -->
              <template #delivery_charge="{ item }">
                <td class="align-middle">
                  <CInput
                    class="mb-0"
                    :lazy="false"
                    :value.sync="item.pivot.delivery_charge"
                    placeholder="0.00"
                    type="number"
                    step="0.01"
                    pattern="^\d+(?:\.\d{1,2})?$"
                    onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"

                    min="0"
                    :max="maxDeliveryCharge"
                    :was-validated="!item.pivot.valid"
                    @input="(value, event) => validateDeliveryCharge(value, item)"
                  >
                    <template #prepend-content><CIcon name="cil-euro" /></template>
                  </CInput>
                </td>
              </template>

              <!-- Minimum Order -->
              <template #minimum_order="{ item }">
                <td class="align-middle">
                  <CInput
                    class="mb-0"
                    :lazy="false"
                    :value.sync="item.pivot.minimum_order"
                    placeholder="0.00"
                    type="number"
                    step="0.01"
                    pattern="^\d+(?:\.\d{1,2})?$"
                    onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"

                    min="0"
                    :max="maxMinimumOrder"
                    :was-validated="!item.pivot.valid"
                    @input="(value, event) => validateMinimumOrder(value, item)"
                  >
                    <template #prepend-content><CIcon name="cil-euro" /></template>
                  </CInput>
                </td>
              </template>

              <!-- Free Charge Above -->
              <template #free_above="{ item }">
                <td class="align-middle">
                  <CInput
                    class="mb-0"
                    :lazy="false"
                    :value.sync="item.pivot.free_above"
                    placeholder="0.00"
                    type="number"
                    step="0.01"
                    pattern="^\d+(?:\.\d{1,2})?$"
                    onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"

                    :min="(item.pivot.minimum_order > 0 && item.pivot.free_above > 0) ? item.pivot.minimum_order : 0"
                    :max="item.pivot.delivery_charge > 0 ? maxFreeAbove : 0"
                    :disabled="item.pivot.delivery_charge <= 0"
                    :was-validated="!item.pivot.valid"
                    @input="(value, event) => validateFreeAbove(value, item)"
                  >
                    <template #prepend-content><CIcon name="cil-euro" /></template>
                  </CInput>
                </td>
              </template>

              <!-- Status -->
              <template #status="{item}">
                <td class="align-middle">
                  <CSwitch
                    labelOn="ACT"
                    labelOff="INA"
                    color="success"
                    shape="pill"
                    :disabled="!item.updatable"
                    :checked.sync="item.enabled"
                    @update:checked="() => updateAvailability(item)"
                  />
                </td>
              </template>

              <!-- Actions -->
              <template #actions="{ item }">
                <td class="align-middle">
                  <CButton
                    size="sm"
                    color="danger"
                    class="ml-1"
                    @click="deleteItem(item)"
                    :disabled="!item.updatable"
                    ><CIcon name="cil-trash"
                  /></CButton>
                </td>
              </template>
            </CDataTable>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CModal
      v-if="mapModal"
      :show.sync="mapModal"
      :closeOnBackdrop="true"
      :centered="false"
      size="xl"
      color="dark"
      title="Restaurant Delivery Areas"
    >
      <map-restaurant-delivery-areas
        ref="deliveryAreasMap"
        :zip_codes.sync="zipCodes"
        :country_id="country"
        :suggested_zip_codes="deliveryZoneGapWarning.suggestedZones"
        :restaurant_name="restaurant_name"
        :restaurant_lat="restaurant_lat"
        :restaurant_lng="restaurant_lng"
        :warning_distance_km="deliveryZoneWarningCircleDistanceKm"
        :max_delivery_distance_km="deliveryZoneMaxDistanceKm"
        @add:zip="addZipCodes"
      />

      <template #footer>
        <CButton @click="mapModal = false" color="dark">Close</CButton>
      </template>
    </CModal>

    <CElementCover :opacity="0.4" v-show="submitted"/>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { flagSet } from '@coreui/icons'
import _debounce from 'lodash/debounce';
import {
  DEFAULT_MAX_SUGGESTION_DISTANCE_KM,
  getDeliveryZoneGapWarnings,
} from "@/helper/deliveryZoneGapWarnings";
import {
  availableDeliveryPostcodeGroups,
  groupDeliveryPostcodes,
  sameDeliveryPostcodeGroup,
} from "@/helper/deliveryPostcodeGroups";

export default {
  name: "zip-codes",
  flagSet,
  components: {
    vSelect,
    MapRestaurantDeliveryAreas: () => import('@/components/MapRestaurantDeliveryAreas'),
  },
  props: {
    active: { default: false, type: Boolean },
    zip_codes: { default: () => [], type: Array },
    restaurant_zip_code: { default: null, type: String },
    restaurant_country: { default: 1, type: Number },
    restaurant_lat: { default: null, type: Number },
    restaurant_lng: { default: null, type: Number },
    restaurant_name: { default: null, type: String },
    delivery_distances: { default: () => { return {} }, type: Object },
  },
  data() {
    return {
      allZipCodes: [],
      selected_zip_code: null,
      selected_row: null,
      loading: false,
      submitted: false,
      mapModal: false,
      maxDistanceNearBy: 10,
      useDeliveryCircleSuggestions: false,
      warningZipCodes: [],
      warningZipCodeFetchKey: null,

      fields: [
        { key: "distance",        label: "Distance (km)",   filter: false, _style: "min-width: 80px" },
        { key: "country",         label: "Country",         filter: false, _style: "min-width: 120px; width: 15%" },
        { key: "zone",            label: "Delivery Zone",   filter: false, _style: "min-width: 200px; width: 20%" },
        { key: "delivery_charge", label: "Delivery Charge", filter: false, _style: "min-width: 120px; width: 20%" },
        { key: "minimum_order",   label: "Minimum Order",   filter: false, _style: "min-width: 120px; width: 20%" },
        { key: "free_above",      label: "Free Charge Above", filter: false, _style: "min-width: 120px; width: 20%" },
        { key: "status",          label: "Status",          filter: false, _style: "min-width: 96px; width: 96px;" },
        { key: "actions",         label: "Actions",         filter: false, sorter: false, _style: "min-width: 84px; width: 84px;" },
      ],
    };
  },

  watch: {
    active: async function (val) {
      if (val) {
        await Promise.all([this.getAllZipCodes()]);
      }
    },

    restaurant_country: [{
      handler: 'countryFilter'
    }],

    restaurant_zip_code: async function (val) {
      await Promise.all([this.changedRestaurantZipCode(val)]);
    },

    selected_row: {
      handler(val) {
        if (!val) {
          return;
        }

        this.zipCodes
          .filter((zc) => sameDeliveryPostcodeGroup(zc, val) && zc.id != val.id)
          .forEach((fe_zc) => {
            fe_zc.pivot.delivery_charge = val.pivot.delivery_charge !== '' ? val.pivot.delivery_charge : 0;
            fe_zc.pivot.free_above = val.pivot.free_above !== '' ? val.pivot.free_above : 0;
            fe_zc.pivot.minimum_order = val.pivot.minimum_order !== '' ? val.pivot.minimum_order : 0;
          });
      },
      deep: true,  // This will let Vue know to look inside the array
      immediate: true //  Also very important the immediate in case you need it, the callback will be called immediately after the start of the observation
    },
  },

  computed: {
    zipCodes: {
      get() {
        return this.zip_codes.map((el) => {
          return {
            ...el,
            country: this.authCountries.find((c) => c.value === el.zcCountryId),
            distance: this.$globalFunc.haversineDistance(
              { lat: this.restaurant_lat, lng: this.restaurant_lng },
              { lat: el.zcLat, lng: el.zcLng }
            ),
          };
        })
        .map((el) => {
          let classes = el.distance > this.maxDistance ? "table-warning" : "";
          classes += el.pivot?.disabled ? " strikeout " : "";

          return {
            ...el,
            enabled: !(el.pivot.disabled),
            updatable: !(el.zcCountryId == this.restaurant_country && el.zcZip == this.restaurant_zip_code),
            _classes: classes,
          };
        });
      },
      set(newValue) {
        this.$emit("update:zip_codes", newValue);
      },
    },
    country() {
      return this.$store.state.filters.country || this.$store.state.countryId
    },
    deliveryZoneRows() {
      return groupDeliveryPostcodes(this.zipCodes);
    },
    maxDistance() {
      return process.env.VUE_APP_DELIVERY_ZONE_DISTANCE_WARNING_LIMIT;
    },

    maxDeliveryCharge() { return 50; },
    maxMinimumOrder() { return 150; },
    maxFreeAbove() { return 150; },
    isValid() { return !this.zipCodes.some(item => item.pivot.valid === false); },

    deliveryZoneGapWarning() {
      return getDeliveryZoneGapWarnings({
        linkedZipCodes: this.zipCodes,
        allZipCodes: this.deliveryZoneGapCandidates,
        restaurantLat: this.restaurant_lat,
        restaurantLng: this.restaurant_lng,
        restaurantCountry: this.restaurant_country,
        maxSuggestionDistanceKm: this.deliveryZoneGapMaximumDistanceKm,
        useDeliveryCircleSuggestions: this.useDeliveryCircleSuggestions,
      });
    },

    deliveryZoneGapMaximumDistanceKm() {
      const enabled = [true, 1, "1", "true"].includes(this.delivery_distances?.max_distance_enabled);
      const meters = Number(String(this.delivery_distances?.max_distance_meter).replace(',', '.'));

      return enabled && Number.isFinite(meters) && meters > 0
        ? meters / 1000
        : DEFAULT_MAX_SUGGESTION_DISTANCE_KM;
    },

    deliveryZoneMaxDistanceKm() {
      const enabled = [true, 1, "1", "true"].includes(this.delivery_distances?.max_distance_enabled);
      const meters = Number(String(this.delivery_distances?.max_distance_meter).replace(',', '.'));

      return enabled && Number.isFinite(meters) && meters > 0
        ? meters / 1000
        : null;
    },

    deliveryZoneWarningCircleDistanceKm() {
      return this.deliveryZoneMaxDistanceKm
        ? null
        : DEFAULT_MAX_SUGGESTION_DISTANCE_KM;
    },

    zipCodeOptions() {
      const byId = new Map();

      this.allZipCodes.forEach((zipCode) => {
        byId.set(zipCode.id, zipCode);
      });

      this.warningZipCodes.forEach((zipCode) => {
        byId.set(zipCode.id, {
          ...(byId.get(zipCode.id) || {}),
          ...zipCode,
        });
      });

      return Array.from(byId.values());
    },

    groupedZipCodeOptions() {
      return availableDeliveryPostcodeGroups(this.zipCodeOptions, this.zipCodes);
    },

    deliveryZoneGapCandidates() {
      return this.hasUsableZipCodeCache(this.allZipCodes)
        ? this.allZipCodes
        : this.warningZipCodes;
    },

    visibleDeliveryZoneGapSuggestions() {
      return this.deliveryZoneGapWarning.suggestedZones.slice(0, 8);
    },

    remainingDeliveryZoneGapSuggestionCount() {
      return Math.max(0, this.deliveryZoneGapWarning.suggestedZones.length - this.visibleDeliveryZoneGapSuggestions.length);
    },

    deliveryZoneGapWarningTitle() {
      return this.useDeliveryCircleSuggestions
        ? "Suggested delivery zones may be missing."
        : "Closer delivery zones may be missing.";
    },

    deliveryZoneGapWarningSummary() {
      if (!this.useDeliveryCircleSuggestions) {
        const farthest = this.deliveryZoneGapWarning.farthestActiveZone;

        if (!farthest) {
          return "";
        }

        const ceiling = this.deliveryZoneGapWarning.maxSuggestionDistanceKm;
        const ceilingText = ceiling === null
          ? ""
          : ` Suggestions are capped at ${ceiling.toFixed(1)} km from the restaurant.`;

        return `A farthest active zone is ${farthest.zcZip} at ${farthest.distance.toFixed(1)} km; these zones are at least ${this.deliveryZoneGapWarning.minGapKm} km closer.${ceilingText}`;
      }

      const ceiling = this.deliveryZoneGapWarning.suggestionDistanceCeilingKm;
      const ceilingText = ceiling === null
        ? "the delivery distance circle"
        : `the ${ceiling.toFixed(1)} km delivery distance circle`;

      return `These zones are not active yet, but their center or saved postcode bounds overlap ${ceilingText}.`;
    }
  },

  async beforeMount() {
    this.changedRestaurantZipCode = _debounce(async (zip_code) => {
      if (this.restaurant_country != this.country || !this.allZipCodes.length) {
        await Promise.all([this.countryFilter(this.restaurant_country)]);
      }
      await Promise.all([this.addZipCodes(zip_code, null, this.restaurant_country)]);
    }, 1000);

    await Promise.all([
      // this.getAllZipCodes(),
      this.changedRestaurantZipCode(this.restaurant_zip_code)
    ]);
  },

  methods: {
    zipCodeCacheKey(country = this.country) {
      return `${country}-restaurant-delivery-zone-zip-codes-v4`;
    },

    hasUsableZipCodeCache(zipCodes) {
      return zipCodes.length && zipCodes.every((zipCode) => (
        Object.prototype.hasOwnProperty.call(zipCode, "zcLat") &&
        Object.prototype.hasOwnProperty.call(zipCode, "zcLng") &&
        Object.prototype.hasOwnProperty.call(zipCode, "bounds")
      ));
    },

    async getAllZipCodes() {
      const cacheKey = this.zipCodeCacheKey();
      const cachedZipCodes = JSON.parse(sessionStorage.getItem(cacheKey)) || [];

      if (this.hasUsableZipCodeCache(cachedZipCodes)) {
        this.allZipCodes = cachedZipCodes;
        return;
      }

      sessionStorage.removeItem(cacheKey);
      this.loading = true;
      await this.$axios
        .get(this.$backend.ZIP_CODES.GET_ALL, {
          params: {
            country: this.country,
            fields: 'zcZip,zcCity,zcLat,zcLng',
          },
        })
        .then((response) => (this.allZipCodes = response.data))
        .finally(() => {
          this.loading = false;
          if (this.hasUsableZipCodeCache(this.allZipCodes)) {
            sessionStorage.setItem(cacheKey, JSON.stringify(this.allZipCodes));
          }
        });

      if (!this.hasUsableZipCodeCache(this.allZipCodes)) {
        await this.getWarningZipCodesNearBy();
      }
    },

    deleteItem(item) {
      this.zipCodes = this.removeZipCodes(this.zipCodes, item.zcZip, item.zcCountryId);
    },

    removeZipCodes(zip_codes, zip, country) {
      return zip_codes.filter(function (item) {
        return (item.zcZip != zip) || (item.zcCountryId != country);
      });
    },

    filteredZipCodes(options, search) {
      const normalizedSearch = search.toLowerCase();

      return search.length
        ? options.filter((option) => (
            String(option.zcZip || "").includes(search) ||
            String(option.zcCity || "").toLowerCase().includes(normalizedSearch)
          ))
        : null;
    },

    selectingZipCodes() {
      if (this.selected_zip_code) {
        this.addZipCodes(
          this.selected_zip_code.zcZip,
          null,
          this.selected_zip_code.zcCountryId
        )
      }
    },

    addZipCodes(zip_code, zip_id = null, country_id = this.country) {
      let newZipCodes = [];

      const filteredZipCodes = zip_id
        ? this.zipCodeOptions.filter((f_zc) => f_zc.id == zip_id)
        : this.zipCodeOptions.filter((f_zc) => (
            String(f_zc.zcZip) === String(zip_code) &&
            Number(f_zc.zcCountryId) === Number(country_id)
          ));

      filteredZipCodes.forEach((fe_zc) => {
        if (!this.zipCodes.some((i_zc) => i_zc.id == fe_zc.id)) {
          const item = {
            id: fe_zc.id,
            zcCity: fe_zc.zcCity,
            zcLat: fe_zc.zcLat,
            zcLng: fe_zc.zcLng,
            zcZip: fe_zc.zcZip,
            zcCountryId: fe_zc.zcCountryId,
            bounds: fe_zc.bounds,
            // country: this.authCountries.find((c) => c.value === fe_zc.zcCountryId),
            // distance: this.$globalFunc.haversineDistance(
            //   { lat: this.restaurant_lat, lng: this.restaurant_lng },
            //   { lat: fe_zc.zcLat, lng: fe_zc.zcLng }
            // ),
            // _classes: fe_zc.distance > this.maxDistance ? 'table-warning' : '',

            pivot: {
              delivery_charge: 0,
              free_above: 0,
              minimum_order: 0,
              disabled: null,
              valid: true
            },
          };

          newZipCodes.push(item);
        }
      });

      this.zipCodes = this.zipCodes.concat(newZipCodes);
      this.getWarningZipCodesNearBy();
    },

    addSuggestedDeliveryZones() {
      const suggestedZones = this.deliveryZoneGapWarning.suggestedZones;
      const sameZone = (item, zone) => (
        Number(item.zcCountryId) === Number(zone.zcCountryId) &&
        String(item.zcZip) === String(zone.zcZip)
      );

      suggestedZones.forEach((zone) => {
        const linkedZone = this.zipCodes.find((item) => sameZone(item, zone));

        if (linkedZone?.pivot?.disabled) {
          this.zipCodes
            .filter((item) => sameZone(item, zone))
            .forEach((item) => {
              item.pivot.disabled = null;
            });

          return;
        }

        this.addZipCodes(zone.zcZip, null, zone.zcCountryId);
      });
    },

    async getZipCodesNearBy() {
      const vm = this;
      this.submitted = true;

      await this.$axios
        .get(this.$backend.ZIP_CODES.NEAR_BY, {
          params: {
            "restaurant-latitude": this.restaurant_lat,
            "restaurant-longitude": this.restaurant_lng,
            "max-distance": this.maxDistanceNearBy,
          },
        })
        .then((response) => {
          response.data.forEach((zone, index) => {
            setTimeout(() => {
              vm.addZipCodes(zone.zcZip, zone.id, zone.zcCountryId);
              // scroll down one page
              window.scrollBy(0, window.innerHeight);
            }, index * 50);
          });
        })
        .finally(() => { this.submitted = false; });
    },

    warningZipCodeRadius() {
      const distances = this.zipCodes
        .map((zipCode) => Number(String(zipCode.distance).replace(',', '.')))
        .filter((distance) => Number.isFinite(distance));
      const defaultRadius = Number(this.maxDistanceNearBy);
      const radius = distances.length
        ? Math.ceil(Math.max(...distances))
        : defaultRadius;
      const cappedRadius = this.deliveryZoneGapMaximumDistanceKm === null
        ? radius
        : Math.min(radius, Math.ceil(this.deliveryZoneGapMaximumDistanceKm));

      return Math.max(1, cappedRadius);
    },

    async getWarningZipCodesNearBy() {
      if (!this.restaurant_lat || !this.restaurant_lng) {
        return;
      }

      const radius = this.warningZipCodeRadius();
      const fetchKey = [
        this.restaurant_country,
        this.restaurant_lat,
        this.restaurant_lng,
        radius,
      ].join('|');

      if (this.warningZipCodeFetchKey === fetchKey && this.warningZipCodes.length) {
        return;
      }

      this.warningZipCodeFetchKey = fetchKey;

      await this.$axios
        .get(this.$backend.ZIP_CODES.NEAR_BY, {
          params: {
            "restaurant-latitude": this.restaurant_lat,
            "restaurant-longitude": this.restaurant_lng,
            "max-distance": radius,
          },
        })
        .then((response) => {
          this.warningZipCodes = response.data.filter((zipCode) => (
            Number(zipCode.zcCountryId) === Number(this.restaurant_country)
          ));
        });
    },

    // Filters
    async countryFilter(value) {
      this.$store.commit("setCountryFilter", value);
      this.selected_zip_code = null;
      await this.getAllZipCodes();
    },

    rangeValidate(item) {
      item.pivot.delivery_charge = Number(item.pivot.delivery_charge);
      item.pivot.minimum_order = Number(item.pivot.minimum_order);
      item.pivot.free_above = Number(item.pivot.free_above);

      return (item.pivot.delivery_charge >= 0 && item.pivot.delivery_charge <= this.maxDeliveryCharge)
        && (item.pivot.minimum_order >= 0 && item.pivot.minimum_order <= this.maxMinimumOrder)
        && (item.pivot.free_above >= 0 && item.pivot.free_above <= this.maxFreeAbove);
    },

    validateDeliveryCharge(value, item) {
      value = Number(value);
      item.pivot.delivery_charge = value;
      let isValid = this.rangeValidate(item);

      isValid &&= value > 0
        ? (item.pivot.free_above >= 0 && item.pivot.free_above <= this.maxFreeAbove)
        : (item.pivot.free_above = 0) == 0;

      item.pivot.valid = isValid;
      return isValid;
    },

    validateMinimumOrder(value, item) {
      value = Number(value);
      item.pivot.minimum_order = value;
      let isValid = this.rangeValidate(item);

      if (value > 0 && item.pivot.free_above > 0) {
        isValid &&= (item.pivot.free_above >= value && item.pivot.free_above <= this.maxFreeAbove)
      }

      item.pivot.valid = isValid;
      return isValid;
    },

    validateFreeAbove(value, item) {
      item.pivot.free_above = Number(value);
      item.pivot.valid = this.validateDeliveryCharge(item.pivot.delivery_charge, item)
        && this.validateMinimumOrder(item.pivot.minimum_order, item);
    },

    // Only called from Restaurant Item
    validate() {
      this.zipCodes.forEach(item => {
        (typeof item.pivot.valid === 'undefined') && this.validateFreeAbove(item.pivot.free_above, item)
      })

      return !this.zipCodes.some(item => item.pivot.valid === false);
    },

    updateAvailability(item) {
      this.zipCodes
        .filter(area => sameDeliveryPostcodeGroup(area, item))
        .forEach(area => area.pivot.disabled = !item.enabled);
    },
  },
};
</script>

<style>
.table-zip-codes th {
  margin-top: 1rem;
}

.delivery-zone-gap-warning {
  border-radius: 6px;
}


.delivery-settings {
  max-width: 900px;
}

.settings-flex {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.settings-group {
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  border: 1px solid #dadada;
  border-radius: 10px;
  transition: box-shadow 0.25s, transform 0.25s;
  padding: 0.5rem;
  margin-bottom: 0.75rem;
}

.settings-group:last-child {
  margin-bottom: 0;
}

.settings-group:hover {
  box-shadow: 0 5px 20px rgba(0,0,0,0.15);
  transform: translateY(-2px) scale(1.01);
}

.setting-item {
  margin-bottom: 0;
}

.setting-item.small-item {
  flex: 0 0 200px;
}

.setting-item.medium {
  flex: 1 1 250px;
}

.setting-item.large {
  flex: 1 1 350px;
}

.vertical-center {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .settings-group {
    padding: 1rem;
  }

  .settings-flex {
    gap: 0rem;
  }

  .setting-item.small-item,
  .setting-item.medium,
  .setting-item.large {
    flex: 1 1 100%;
  }
}
</style>
